import { type FC } from "react";
import styled from "styled-components";

import FilterChip from "../filter-chip/FilterChip";
import Input from "../form-item/Input";
import Toggle from "../form-item/Toggle";
import type {
  TranscriptionMode,
  TranscriptionOptions,
} from "../../app/services/mediaTranscription";

/**
 * 文字起こしの実行条件（音声の種類 / 話者分離 / 語彙ヒント）を選ぶフォーム。
 *
 * 単体（`MediaTranscriptionModal`）と一括（`BulkTranscriptionModal`）で
 * 同じ選択肢・同じ説明文を出すために切り出している。状態は親が持つ
 * （単体の「再実行」は前回の条件を初期値に戻すため）。
 *
 * 言語は自動判定固定なのでフォームには出さない（BE の既定が `auto`）。
 */

export type TranscriptionOptionsValue = {
  mode: TranscriptionMode;
  speakerLabels: boolean;
  promptHint: string;
};

/** 語彙ヒントの最大文字数（BE の MAX_PROMPT_HINT_CHARS と揃える。超過分は切られる） */
const PROMPT_HINT_MAX_LENGTH = 200;

export const DEFAULT_TRANSCRIPTION_OPTIONS: TranscriptionOptionsValue = {
  mode: "conversation",
  speakerLabels: false,
  promptHint: "",
};

/**
 * フォームの値を API に渡すオプションへ変換する。
 * 話者分離と語彙ヒントは互いに排他（話者分離は会話モード = AWS Transcribe、
 * 語彙ヒントは歌唱モード = Whisper の機能）なので、モードに応じて落とす。
 */
export const toTranscriptionOptions = (
  value: TranscriptionOptionsValue,
): TranscriptionOptions => {
  const isLyrics = value.mode === "lyrics";
  return {
    languageCode: "auto",
    mode: value.mode,
    speakerLabels: isLyrics ? false : value.speakerLabels,
    promptHint: isLyrics ? value.promptHint.trim() : undefined,
  };
};

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const OptionNote = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
  white-space: pre-line;
  text-align: center;
`;

/** 歌唱モードの語彙ヒント入力。説明文と同じ幅に収める。 */
const HintInputRow = styled.div`
  width: 100%;
  max-width: 360px;
`;

export interface TranscriptionOptionsFormProps {
  value: TranscriptionOptionsValue;
  onChange: (value: TranscriptionOptionsValue) => void;
  disabled?: boolean;
}

const TranscriptionOptionsForm: FC<TranscriptionOptionsFormProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const isLyricsMode = value.mode === "lyrics";

  const setMode = (mode: TranscriptionMode) => {
    if (disabled) return;
    onChange({ ...value, mode });
  };

  return (
    <>
      <OptionRow>
        <FilterChip
          selected={value.mode === "conversation"}
          onClick={() => setMode("conversation")}
        >
          会話
        </FilterChip>
        <FilterChip selected={isLyricsMode} onClick={() => setMode("lyrics")}>
          歌唱・楽曲
        </FilterChip>
      </OptionRow>
      <OptionNote>
        {isLyricsMode
          ? "BGM の乗った歌に強いエンジンで解析します。話者の分離はできません。"
          : "会話向けのエンジンで解析します。歌の書き起こしは苦手です。"}
      </OptionNote>
      {isLyricsMode ? (
        <HintInputRow>
          <Input
            label="曲名・アーティスト名"
            option="optional"
            placeholder="例: 夜明けのワルツ / SOREMO BAND"
            maxlength={PROMPT_HINT_MAX_LENGTH}
            value={value.promptHint}
            onChange={(e) =>
              !disabled && onChange({ ...value, promptHint: e.target.value })
            }
          />
        </HintInputRow>
      ) : (
        <OptionRow>
          <Toggle
            value={value.speakerLabels}
            onClick={() =>
              !disabled &&
              onChange({ ...value, speakerLabels: !value.speakerLabels })
            }
          >
            話者を分離する
          </Toggle>
        </OptionRow>
      )}
    </>
  );
};

export default TranscriptionOptionsForm;
